// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## PianoCreation
//
// Include description of object & useful info here, please!
import utils from '../utils.js';
import InteractivePrototype, {
  type InteractiveOptions,
} from './interactivePrototype.js';
import CheckMark from './checkMark.js';
import { ordinal } from 'shared/src/utils/ordinal';

interface PianoCreationOptions extends InteractiveOptions {
  octaves?: number;
}
const svgNS = 'http://www.w3.org/2000/svg';

class PianoCreation extends InteractivePrototype {
  declare options: PianoCreationOptions;

  constructor(displayDiv: HTMLDivElement, options: PianoCreationOptions) {
    super(displayDiv, options);
    this.targetSpeedFromSkills = PianoCreation.registry.skills;
    this.settings = {
      instructions: true,
      letters: { letterButtons: false, checkButton: true, hotKeys: false },
      checkMark: {},
      player: {},
      progressTracker: true,
      oneTryOnly: false,
    };

    this.init(displayDiv, options);
  }

  init(displayDiv: HTMLDivElement, options: PianoCreationOptions) {
    // copy our settings onto the prototype settings:
    console.log('this.settings: ');
    console.log(this.settings);

    // setup default options:
    this.options = { octaves: 1 };
    utils.merge(this.options, options);

    // setup global variables:
    this.blackNotesOn = [];

    // now call the prototype's init function:
    return super.init(displayDiv, this.options);
  }

  generateQuestions() {
    this.questions = [];
  }

  newQuestion() {
    this.instructionsDiv!.innerHTML =
      'Add black notes below to create the layout of the piano.';
    this.progressTracker?.start(this.targetSpeed);
  }

  createCustomDivs() {
    this.createDiv('pianoDiv', { margin: '0 auto', textAlign: 'center' });
    this.displayDiv.removeChild(this.pianoDiv);
    this.displayDiv.insertBefore(this.pianoDiv, this.lettersDiv!);
    this.checkMark = new CheckMark(this.pianoDiv);
  }

  initializeCustomElements() {
    this.draw();
    this.layout.customDiv = {
      div: this.pianoDiv,
      visible: true,
      percent: 65,
      callback: (height: number) => this.resizePiano(height),
    };
  }

  addListeners() {
    this.letterPanel!.addCheckCallback(this.check, this);
  }

  // ## Checking Functions:

  check() {
    if (this.rightAnswerDelay) {
      return;
    }
    const rightAnswer = (index: number) => {
      const goalAnswer =
        index % 7 === 0 ||
        index % 7 === 1 ||
        index % 7 === 3 ||
        index % 7 === 4 ||
        index % 7 === 5;

      const on =
        typeof this.blackNotesOn[index] !== 'undefined' &&
        this.blackNotesOn[index];

      return on === goalAnswer;
    };

    let allCorrect = true;
    this.blackNotes.forEach((note, i) => {
      if (!rightAnswer(i)) {
        allCorrect = false;
        note.setAttributeNS(null, 'fill', 'red');
        if (this.blackNotesOn[i]) {
          note.setAttributeNS(null, 'opacity', '0.25');
        } else {
          note.setAttributeNS(null, 'opacity', '1');
        }
      }
    });

    if (allCorrect) {
      this.rightAnswer();
    } else {
      this.wrongAnswer();
    }
  }

  rightAnswer() {
    this.progressTracker?.rightAnswer(1, true);
    this.checkMark?.check();
    this.gameOver();
    // Checkmark & goodjob text
    if (this.lettersDiv) {
      this.displayDiv.removeChild(this.lettersDiv);
    }
    // Remove event listeners:
    const newElement = this.svg!.cloneNode(true);
    this.svg!.parentNode!.replaceChild(newElement!, this.svg!);
    // Congratulations:
    this.instructionsDiv!.innerHTML = 'Nice work!';
    this.instructionsDiv!.setAttribute('role', 'alert');
  }

  wrongAnswer() {
    window.setTimeout(() => {
      this.recolorBlackNotes();
    }, 2000);
    this.progressTracker?.wrongAnswer(1, false);
  }

  recolorBlackNotes() {
    this.blackNotes.forEach((note, i) => {
      this.applyAttributes(note, this.blackNoteStyle);
      if (this.blackNotesOn[i]) {
        note.setAttributeNS(null, 'opacity', '1');
      } else {
        note.setAttributeNS(null, 'opacity', '0');
      }
    });
  }

  whiteNoteWidth = 30;

  get whiteNoteHeight() {
    return this.whiteNoteWidth * 5;
  }

  get blackNoteWidth() {
    return this.whiteNoteWidth * 0.552;
  }

  get blackNoteHeight() {
    return this.whiteNoteHeight * 0.666;
  }

  whiteNoteStyle = {
    stroke: 'black',
    'stroke-width': '1px',
    fill: 'white',
  };

  blackNoteStyle = {
    stroke: 'black',
    'stroke-width': '2px',
    fill: '#313131',
  };

  declare pianoDiv: HTMLDivElement;

  svg?: SVGSVGElement;

  totalWhiteNotes = 7;

  width = 210;
  height = 30 * 5;

  blackNotes: SVGPathElement[] = [];
  blackNoteLinks: SVGAElement[] = [];
  blackNotesOn: boolean[] = [];

  // ## Drawing Functions:
  draw() {
    // clear the canvas:
    this.blackNotes = [];
    this.blackNoteLinks = [];
    this.pianoDiv.innerHTML = '';

    this.totalWhiteNotes = (this.options.octaves ?? 1) * 7;

    // create the SVG:
    const svg = document.createElementNS(svgNS, 'svg');
    this.svg = svg;

    this.width = this.displayDiv.scrollWidth - 1;
    if (this.displayDiv.clientWidth === 0) {
      this.width = 500;
    }
    console.log(`Width: ${this.width}`);
    this.whiteNoteWidth = Math.floor(this.width / this.totalWhiteNotes);

    // if that's taller than our height, re-scale based on height:
    console.log(`this.height: ${this.height}`);
    console.log(`this.displayDiv.clientHeight${this.displayDiv.clientHeight}`);
    if (
      typeof this.height !== 'undefined' &&
      this.whiteNoteHeight > this.height
    ) {
      // rescale: whiteNoteHeight = whiteNoteWidth * 5, so...
      this.whiteNoteWidth = (this.height - 2) / 5;
    } else if (
      this.displayDiv.clientHeight !== 0 &&
      this.whiteNoteHeight > this.displayDiv.clientHeight
    ) {
      this.whiteNoteWidth = (this.displayDiv.clientHeight - 2) / 5;
    }

    this.blackNoteStyle['stroke-width'] = `${this.blackNoteWidth / 20}px`;

    this.width = this.whiteNoteWidth * this.totalWhiteNotes;
    svg.setAttribute('width', `${this.width + 1}`);

    svg.setAttribute('height', `${this.whiteNoteHeight}px`);

    svg.setAttribute('viewBox', `0 0 ${this.width} ${this.whiteNoteHeight}`);

    const octaves = this.options.octaves ?? 1;

    for (let i = 0; i < octaves; i++) {
      this.drawWhiteOctave(i);
    }
    for (let i = 0; i < octaves; i++) {
      this.drawBlackOctave(i);
    }

    this.pianoDiv.appendChild(this.svg);
  }

  resizePiano(maxHeight: number) {
    this.height = maxHeight;
    this.draw();
    return maxHeight - this.whiteNoteHeight;
  }

  drawWhiteOctave(octave: number) {
    const octaveWidth = this.whiteNoteWidth * 7;
    const thisOctaveOffset = octave * octaveWidth;

    for (let i = 0; i < 7; i++) {
      const x = this.whiteNoteWidth * i + thisOctaveOffset;
      this.drawWhiteNote(x);
      this.drawWhiteNote(x);
    }
  }

  drawBlackOctave(octave: number) {
    const octaveWidth = this.whiteNoteWidth * 7;
    const thisOctaveOffset = octave * octaveWidth;

    // Draw the black notes:
    for (let i = 0; i < 7; i++) {
      const x =
        this.whiteNoteWidth * (i + 1) +
        thisOctaveOffset -
        this.blackNoteWidth / 2;
      const index = i + octave * 7;
      this.drawBlackNote(index, x);
    }
  }

  drawWhiteNote(x: number) {
    const options = {};
    utils.merge(options, this.whiteNoteStyle);

    const rect = this.drawRect(
      x,
      0,
      this.whiteNoteWidth,
      this.whiteNoteHeight - 1,
      options,
    );
    this.svg?.appendChild(rect);
  }

  drawBlackNote(index: number, x: number) {
    const options = {};
    utils.merge(options, this.blackNoteStyle);

    const a = document.createElementNS(svgNS, 'a');
    this.blackNoteLinks[index] = a;

    const rect = this.drawRect(
      x,
      -2,
      this.blackNoteWidth,
      this.blackNoteHeight + 2,
      options,
    );

    a.appendChild(rect);

    this.addEventListeners(a, rect, index);

    this.blackNotes[index] = rect;
    if (this.blackNotesOn[index] === true) {
      this.applyAttributes(rect, { opacity: '1' });
    } else {
      this.applyAttributes(rect, { opacity: '0' });
    }

    this.svg?.appendChild(a);
  }

  drawRect(
    x: number,
    y: number,
    w: number,
    h: number,
    attributes: Record<string, string>,
  ) {
    const points: (
      | { x: number; y: number; command: 'M' | 'L' }
      | {
          x: number;
          y: number;
          command: 'C';
          beziers: [{ x: number; y: number }, { x: number; y: number }];
        }
    )[] = [
      { x: 0, y: 0, command: 'M' },
      { x: 0, y: 95, command: 'L' },
      {
        x: 10,
        y: 100,
        command: 'C',
        beziers: [
          { x: 0, y: 100 },
          { x: 0, y: 100 },
        ],
      },
      { x: 90, y: 100, command: 'L' },
      {
        x: 100,
        y: 95,
        command: 'C',
        beziers: [
          { x: 100, y: 100 },
          { x: 100, y: 100 },
        ],
      },
      { x: 100, y: 0, command: 'L' },
      { x: 0, y: 0, command: 'L' },
    ];

    let pointsList = '';
    let spacer = '';

    function translate(point: { x: number; y: number }) {
      point.x = (point.x * w) / 100 + x;
      point.y = (point.y * h) / 100 + y;
    }

    points.forEach((point) => {
      translate(point);
      let guide1;
      let guide2;
      switch (point.command) {
        case 'C':
          guide1 = point.beziers[0];
          guide2 = point.beziers[1];
          translate(guide1);
          translate(guide2);
          pointsList += `${spacer}C ${guide1.x} ${guide1.y}, ${guide2.x} ${guide2.y}, ${point.x} ${point.y}`;
          break;
        default:
          pointsList += `${spacer + point.command} ${point.x} ${point.y}`;
          break;
      }
      spacer = ' ';
    }, this);

    const path = document.createElementNS(svgNS, 'path');
    path.setAttributeNS(null, 'd', pointsList);

    this.applyAttributes(path, attributes);

    return path;
  }

  applyAttributes(element: SVGElement, attributes: Record<string, string>) {
    Object.entries(attributes).forEach(([propertyName, propertyValue]) => {
      element.setAttributeNS(null, propertyName, propertyValue);
    });
  }

  addEventListeners(a: SVGAElement, rect: SVGPathElement, index: number) {
    a.setAttributeNS(null, 'role', 'switch');
    a.removeAttributeNS(null, 'aria-checked');
    a.setAttributeNS(null, 'tabindex', '0');
    a.setAttributeNS(
      null,
      'title',
      `Black note between the ${ordinal(index + 1)} and ${ordinal(
        index + 2,
      )} white notes.`,
    );

    a.addEventListener('keypress', (e) => {
      console.log(e, e.key);
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        this.toggle(index);
      }
    });

    a.addEventListener(
      'click',
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggle(index);
      },
      false,
    );

    // rect.addEventListener(
    //   'mousedown',
    //   (e) => {
    //     e.preventDefault();
    //     this.toggle(index);
    //   },
    //   false,
    // );

    a.addEventListener(
      'mouseenter',
      (e) => {
        e.preventDefault();
        this.hoverEnter(index);
      },
      false,
    );

    a.addEventListener(
      'mouseout',
      (e) => {
        e.preventDefault();
        this.hoverExit(index);
      },
      false,
    );

    a.addEventListener('focus', (e) => {
      e.preventDefault();
      this.hoverEnter(index);
    });

    a.addEventListener('blur', (e) => {
      e.preventDefault();
      this.hoverExit(index);
    });
  }

  toggle(index: number) {
    const a = this.blackNoteLinks[index];
    if (this.blackNotesOn[index]) {
      this.blackNotes[index]?.setAttributeNS(null, 'opacity', '0.0');
      this.blackNotesOn[index] = false;
      a?.removeAttributeNS(null, 'aria-checked');
    } else {
      this.blackNotes[index]?.setAttributeNS(null, 'opacity', '1');
      this.blackNotesOn[index] = true;
      a?.setAttributeNS(null, 'aria-checked', 'true');
    }
  }

  hoverEnter(index: number) {
    if (!this.blackNotesOn[index]) {
      this.blackNotes[index]?.setAttributeNS(null, 'opacity', '0.3');
    }
  }

  hoverExit(index: number) {
    if (!this.blackNotesOn[index]) {
      this.blackNotes[index]?.setAttributeNS(null, 'opacity', '0');
    }
  }

  checkAnswer() {
    // intentionally empty
  }

  static registry = {
    description: 'Create the Layout of the Piano',
    skills: ['piano_layout'],
  };
}

export default PianoCreation;
